import { useRef } from "react";
import { serverNow } from "@/lib/api";
import { cardBackSrc, weatherCardSrc, globalCardSrc, CARD_ASPECT } from "@/lib/artAssets";
import { FaceChip } from "@/components/game/WeatherDice";

// WEATHER CARD REVEAL (replaces the weather wheel). Two cards on the table, the same on every
// screen (server time, timeline: useWeatherTimeline):
//   the WEATHER card turns over from its back at once, holds, and then
//   the GLOBAL card, which was lying face down next to it, turns over and shows what it does to
//   the carbon meter ("warms the meter by 3" / "adds 2 to the shared reserve").
// A screen that loads late joins the animation where it is now. Reduced motion: no turning.

// One card that turns over `startMs` after the draw. The delay is worked out once, when the card
// appears: a late screen starts in the middle of the turn (or shows the face at once).
export function FlipCard({ backSrc, frontSrc, alt, width, startMs, drawnAtMs, animate = true, durationMs = 620, testid, faceTestid }) {
  const delayRef = useRef(null);
  if (delayRef.current === null) delayRef.current = Math.round(startMs - (serverNow() - drawnAtMs));
  const turning = animate && frontSrc;
  const face = { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", borderRadius: 10, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", boxShadow: "0 6px 18px rgba(36,40,38,.28)" };
  return (
    <div data-testid={testid} style={{ width, aspectRatio: CARD_ASPECT, perspective: 900 }}>
      <div
        className={turning ? "event-card-flip" : undefined}
        style={{
          position: "relative", width: "100%", height: "100%", transformStyle: "preserve-3d",
          animationDuration: `${durationMs}ms`, animationDelay: `${delayRef.current}ms`,
          transform: turning ? undefined : frontSrc ? "none" : "rotateY(180deg)",
        }}
      >
        <img decoding="async" src={frontSrc || backSrc} alt={alt} data-testid={faceTestid} draggable={false} className="select-none" style={face} />
        <img decoding="async" src={backSrc} alt="" aria-hidden="true" draggable={false} className="select-none" style={{ ...face, transform: "rotateY(180deg)" }} />
      </div>
    </div>
  );
}

const CAP = { fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)" };

export function globalEffectText(card) {
  if (!card?.amount) return "";
  return card.effect === "warming"
    ? `+${card.amount} warms the meter`
    : `−${card.amount} into the shared reserve`;
}

export default function WeatherCardReveal({ ws, timeline, areaLine }) {
  const phase = timeline.phase;
  const showWeather = phase !== "flipping" || !timeline.live;
  const globalUp = phase === "global" || phase === "reveal" || phase === "results";
  const g = ws?.global_card;
  const animate = !timeline.reduced;
  const key = timeline.key;
  return (
    <div className="flex flex-col items-center" style={{ gap: 10 }}>
      <div className="flex items-start justify-center" style={{ gap: 14 }}>
        <div className="flex flex-col items-center" style={{ gap: 5 }}>
          <span style={CAP}>Weather</span>
          {ws ? (
            <FlipCard
              key={`w|${key}`}
              testid="weather-card"
              faceTestid="weather-card-face"
              backSrc={cardBackSrc("weather")}
              frontSrc={weatherCardSrc(ws.face)}
              alt={ws.face_name}
              width={150}
              startMs={timeline.offsets.weatherFlip}
              drawnAtMs={timeline.drawnAtMs}
              animate={animate}
            />
          ) : (
            <FlipCard key="w|wait" testid="weather-card" backSrc={cardBackSrc("weather")} frontSrc={null} alt="Weather card" width={150} startMs={0} drawnAtMs={0} animate={false} />
          )}
        </div>
        {g?.no && (
          <div className="flex flex-col items-center" style={{ gap: 5 }}>
            <span style={CAP}>Global</span>
            <FlipCard
              key={`g|${key}`}
              testid="global-card"
              faceTestid="global-card-face"
              backSrc={cardBackSrc("global")}
              frontSrc={animate || globalUp ? globalCardSrc(g) : null}
              alt={g.name}
              width={150}
              startMs={timeline.offsets.globalFlip}
              drawnAtMs={timeline.drawnAtMs}
              animate={animate}
            />
          </div>
        )}
      </div>
      <div className="flex flex-col items-center text-center" style={{ minHeight: 62, gap: 4 }}>
        {ws && showWeather ? (
          <>
            <div className="flex items-center" style={{ gap: 8 }} data-testid="weather-overlay-result" data-face={ws.face}>
              <FaceChip faceKey={ws.face} size={26} radius={6} />
              <span style={{ fontSize: 22, fontWeight: 800, color: "var(--ink)" }}>{ws.face_name}</span>
            </div>
            <div data-testid="weather-overlay-area" style={{ fontSize: 12.5, lineHeight: "16px", color: "var(--ink-3)", maxWidth: 380 }}>{areaLine}</div>
          </>
        ) : (
          <div data-testid="weather-overlay-drawing" style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-3)", marginTop: 8 }}>Drawing the weather…</div>
        )}
        {g?.no && globalUp && (
          <div data-testid="weather-overlay-global" data-effect={g.effect} className="flex items-baseline justify-center flex-wrap" style={{ gap: 6, fontSize: 13, color: "var(--ink-2)" }}>
            <b style={{ color: "var(--ink)" }}>{g.name}</b>
            <span style={{ fontWeight: 700, color: g.effect === "warming" ? "#B5532E" : "var(--sea)" }}>{globalEffectText(g)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
